import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tasks, users, time, clients } from '@/lib/api';
import { formatDate, formatDuration, getPriorityColor } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Play, CheckCircle2, Clock, AlertCircle, 
  MoreVertical, Trash2, Edit, Calendar, Briefcase, User, Filter, Download, Building,
  MessageSquare, ArrowUpDown, List, LayoutGrid, Send
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';

const priorityLabels = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
};

const statusLabels = {
  pending: 'Pendiente',
  in_progress: 'En Progreso',
  completed: 'Completada',
};

const statusIcons = {
  pending: AlertCircle,
  in_progress: Clock,
  completed: CheckCircle2,
};

const VERTICALS = {
  logistics: { label: 'Servicios Logísticos', color: 'text-blue-600 bg-blue-50' },
  consulting: { label: 'Consultoría', color: 'text-violet-600 bg-violet-50' },
  technology: { label: 'Tecnología', color: 'text-emerald-600 bg-emerald-50' },
};

function TaskCard({ task, onUpdate, onRequestDelete, onStartTimer, onOpenDetail, usersList, currentUser }) {
  const StatusIcon = statusIcons[task.status] || AlertCircle;
  const priorityColor = getPriorityColor(task.priority);
  const isCreator = task.created_by === currentUser?.user_id;
  const isAdmin = currentUser?.role === 'admin';
  const isSupervisor = (task.supervisors || []).includes(currentUser?.user_id);
  const isIovar = currentUser?.email === 'imedina@transtar.io';
  const canChangeStatus = isCreator || isAdmin || isSupervisor || isIovar;
  const pendingRequests = (task.status_change_requests || []).filter(r => r.status === 'pending').length;
  
  // Time since creation
  const createdDate = new Date(task.created_at);
  const daysSinceCreation = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

  const statusColors = {
    pending: 'badge-danger',
    in_progress: 'badge-warning',
    completed: 'badge-success',
  };

  const assignedUsers = task.assigned_users || [];

  return (
    <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer" onClick={() => onOpenDetail(task)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <StatusIcon className={`h-4 w-4 ${
                task.status === 'completed' ? 'text-emerald-500' :
                task.status === 'in_progress' ? 'text-amber-500' : 'text-rose-500'
              }`} />
              <h3 className="font-semibold truncate">{task.title}</h3>
              {pendingRequests > 0 && canChangeStatus && (
                <Badge className="bg-amber-500 text-white text-xs">{pendingRequests} solicitud</Badge>
              )}
            </div>
            {task.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{task.description}</p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge variant="outline" className={statusColors[task.status]}>{statusLabels[task.status]}</Badge>
              <Badge variant="outline" className={`badge-${priorityColor}`}>{priorityLabels[task.priority]}</Badge>
              {task.vertical && VERTICALS[task.vertical] && (
                <Badge variant="outline" className={VERTICALS[task.vertical].color}>
                  <Briefcase className="h-3 w-3 mr-1" />{VERTICALS[task.vertical].label}
                </Badge>
              )}
              {task.client_name && (
                <Badge variant="outline" className="gap-1"><Building className="h-3 w-3" />{task.client_name}</Badge>
              )}
              {task.due_date && (
                <Badge variant="outline" className="gap-1"><Calendar className="h-3 w-3" />{formatDate(task.due_date)}</Badge>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              {assignedUsers.length > 0 && (
                <span>Asignado: {assignedUsers.map(u => u.name).join(', ')}</span>
              )}
              <span>{daysSinceCreation}d activa</span>
              {task.actual_hours > 0 && <span>{formatDuration(task.actual_hours * 60)} trabajado</span>}
            </div>
          </div>
          
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {task.status !== 'completed' && (
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onStartTimer(task)} data-testid={`start-timer-${task.task_id}`}>
                <Play className="h-4 w-4" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canChangeStatus && task.status !== 'completed' && (
                  <DropdownMenuItem onClick={() => onUpdate(task.task_id, { status: 'completed' })}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />Marcar completada
                  </DropdownMenuItem>
                )}
                {canChangeStatus && task.status === 'pending' && (
                  <DropdownMenuItem onClick={() => onUpdate(task.task_id, { status: 'in_progress' })}>
                    <Clock className="h-4 w-4 mr-2" />En progreso
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onOpenDetail(task)}>
                  <Edit className="h-4 w-4 mr-2" />Ver / Editar
                </DropdownMenuItem>
                {isCreator && (
                  <DropdownMenuItem className="text-destructive" onClick={() => onRequestDelete(task)}>
                    <Trash2 className="h-4 w-4 mr-2" />Eliminar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateTaskDialog({ open, onOpenChange, onSubmit, usersList, clientsList, prefilledClientId }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    assigned_to: [],
    supervisors: [],
    due_date: '',
    estimated_hours: '',
    vertical: '',
    client_id: '',
  });

  // Set prefilled client when dialog opens
  useEffect(() => {
    if (open && prefilledClientId) {
      setFormData(prev => ({ ...prev, client_id: prefilledClientId }));
    }
  }, [open, prefilledClientId]);

  const toggleUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(userId)
        ? prev.assigned_to.filter(id => id !== userId)
        : [...prev.assigned_to, userId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Debes seleccionar una empresa o "Ninguna empresa"');
      return;
    }
    const data = { ...formData };
    if (data.estimated_hours) {
      data.estimated_hours = parseFloat(data.estimated_hours);
    } else {
      delete data.estimated_hours;
    }
    if (data.due_date) {
      data.due_date = new Date(data.due_date).toISOString();
    } else {
      delete data.due_date;
    }
    if (!data.assigned_to || data.assigned_to.length === 0) {
      delete data.assigned_to;
    }
    if (!data.vertical || data.vertical === 'none') {
      delete data.vertical;
    }
    if (!data.client_id || data.client_id === 'none') {
      delete data.client_id;
    }
    if (!data.supervisors || data.supervisors.length === 0) {
      delete data.supervisors;
    }
    onSubmit(data);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      status: 'pending',
      assigned_to: [],
      supervisors: [],
      due_date: '',
      estimated_hours: '',
      vertical: '',
      client_id: '',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Nueva Tarea</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="title">Titulo *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="task-title-input"
            />
          </div>
          
          <div className="space-y-1.5">
            <Label htmlFor="description">Descripcion</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              data-testid="task-description-input"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Prioridad</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baja</SelectItem>
                  <SelectItem value="medium">Media</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Vertical</Label>
              <Select
                value={formData.vertical}
                onValueChange={(value) => setFormData({ ...formData, vertical: value })}
              >
                <SelectTrigger data-testid="task-vertical-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin vertical</SelectItem>
                  {Object.entries(VERTICALS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Empresa *</Label>
              <Select
                value={formData.client_id}
                onValueChange={(value) => setFormData({ ...formData, client_id: value })}
              >
                <SelectTrigger data-testid="task-client-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguna</SelectItem>
                  {(clientsList || []).map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Asignar a ({formData.assigned_to.length})</Label>
              <div className="border rounded-md p-1.5 max-h-24 overflow-y-auto space-y-0.5">
                {usersList.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No hay usuarios</p>
                ) : (
                  usersList.map((user) => (
                    <label key={user.user_id} className="flex items-center gap-2 p-0.5 rounded hover:bg-muted cursor-pointer">
                      <input type="checkbox" checked={formData.assigned_to.includes(user.user_id)} onChange={() => toggleUser(user.user_id)} className="rounded" />
                      <span className="text-sm">{user.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Supervisores ({formData.supervisors.length})</Label>
              <div className="border rounded-md p-1.5 max-h-24 overflow-y-auto space-y-0.5">
                {usersList.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2 p-0.5 rounded hover:bg-muted cursor-pointer">
                    <input type="checkbox" checked={formData.supervisors.includes(u.user_id)} onChange={() => setFormData(prev => ({
                      ...prev,
                      supervisors: prev.supervisors.includes(u.user_id)
                        ? prev.supervisors.filter(id => id !== u.user_id)
                        : [...prev.supervisors, u.user_id]
                    }))} className="rounded" />
                    <span className="text-sm">{u.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Iovar Medina siempre incluido</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="due_date">Fecha limite</Label>
              <Input id="due_date" type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} data-testid="task-due-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="estimated_hours">Horas estimadas</Label>
              <Input id="estimated_hours" type="number" step="0.5" min="0" value={formData.estimated_hours} onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })} data-testid="task-hours-input" />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" data-testid="task-submit-btn">
              Crear Tarea
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasksList, setTasksList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefilledClientId, setPrefilledClientId] = useState('');
  const [filter, setFilter] = useState('all');
  const [verticalFilter, setVerticalFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [clientFilter, setClientFilter] = useState('all');
  const [detailTask, setDetailTask] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState('');
  const [statusRequestReason, setStatusRequestReason] = useState('');
  const [statusRequestTarget, setStatusRequestTarget] = useState('');
  const [viewMode, setViewMode] = useState('kanban');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  // Auto-open dialog from URL params (e.g. from Empresas page)
  useEffect(() => {
    if (!loading && searchParams.get('new') === '1') {
      const cid = searchParams.get('client_id') || '';
      setPrefilledClientId(cid);
      setDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [loading, searchParams]);

  const loadData = async () => {
    try {
      const [tasksRes, usersRes, clientsRes] = await Promise.all([
        tasks.getAll(),
        users.getAll(),
        clients.getAll(),
      ]);
      setTasksList(tasksRes.data);
      setUsersList(usersRes.data);
      setClientsList(clientsRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Error al cargar las tareas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await tasks.create(data);
      toast.success('Tarea creada exitosamente');
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error('Error al crear la tarea');
    }
  };

  const handleUpdate = async (taskId, data) => {
    try {
      await tasks.update(taskId, data);
      toast.success('Tarea actualizada');
      loadData();
    } catch (err) {
      toast.error('Error al actualizar la tarea');
    }
  };

  const handleRequestDelete = (task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async (taskId) => {
    try {
      await tasks.delete(taskId);
      toast.success('Tarea eliminada');
      loadData();
    } catch (err) {
      toast.error('Error al eliminar la tarea');
    }
  };

  const handleStartTimer = async (task) => {
    try {
      await time.start({ task_id: task.task_id, description: task.title });
      toast.success('Temporizador iniciado');
      if (task.status === 'pending') {
        // Only creator/admin can change status via update
        if (task.created_by === user?.user_id || user?.role === 'admin') {
          await tasks.update(task.task_id, { status: 'in_progress' });
        }
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar temporizador');
    }
  };

  const handleOpenDetail = (task) => {
    setDetailTask(task);
    setEditingNotes(task.notes || '');
    setDetailOpen(true);
    setStatusRequestReason('');
    setStatusRequestTarget('');
  };

  const handleSaveNotes = async () => {
    if (!detailTask) return;
    try {
      await tasks.updateNotes(detailTask.task_id, editingNotes);
      toast.success('Notas actualizadas');
      loadData();
      // Refresh detail
      const updated = { ...detailTask, notes: editingNotes };
      setDetailTask(updated);
    } catch (err) {
      toast.error('Error al guardar notas');
    }
  };

  const handleRequestStatusChange = async () => {
    if (!detailTask || !statusRequestTarget || !statusRequestReason) {
      toast.error('Selecciona un estatus y escribe el razonamiento');
      return;
    }
    try {
      await tasks.requestStatusChange(detailTask.task_id, statusRequestTarget, statusRequestReason);
      toast.success('Solicitud de cambio de estatus enviada');
      setStatusRequestReason('');
      setStatusRequestTarget('');
      loadData();
    } catch (err) {
      toast.error('Error al enviar solicitud');
    }
  };

  const handleApproveStatusChange = async (taskId, requestId, approve) => {
    try {
      await tasks.approveStatusChange(taskId, requestId, approve);
      toast.success(approve ? 'Estatus cambiado' : 'Solicitud rechazada');
      loadData();
      setDetailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await tasks.exportExcel(
        filter !== 'all' ? filter : undefined,
        verticalFilter !== 'all' ? verticalFilter : undefined
      );
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tareas_transtar_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Tareas exportadas');
    } catch (err) {
      toast.error('Error al exportar tareas');
    }
  };

  const filteredTasks = tasksList.filter((task) => {
    if (filter !== 'all' && task.status !== filter) return false;
    if (verticalFilter !== 'all' && task.vertical !== verticalFilter) return false;
    if (userFilter !== 'all') {
      const assignedIds = task.assigned_to || [];
      if (!assignedIds.includes(userFilter)) return false;
    }
    if (clientFilter !== 'all') {
      if (clientFilter === 'none') {
        if (task.client_id) return false;
      } else if (task.client_id !== clientFilter) return false;
    }
    return true;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortTasks = (list) => {
    return [...list].sort((a, b) => {
      let valA, valB;
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
      switch (sortField) {
        case 'title':
          valA = (a.title || '').toLowerCase();
          valB = (b.title || '').toLowerCase();
          break;
        case 'priority':
          valA = priorityOrder[a.priority] ?? 4;
          valB = priorityOrder[b.priority] ?? 4;
          break;
        case 'due_date':
          valA = a.due_date || 'z';
          valB = b.due_date || 'z';
          break;
        case 'client_name':
          valA = (a.client_name || 'zzz').toLowerCase();
          valB = (b.client_name || 'zzz').toLowerCase();
          break;
        case 'assigned':
          valA = (a.assigned_name || 'zzz').toLowerCase();
          valB = (b.assigned_name || 'zzz').toLowerCase();
          break;
        default:
          valA = a.created_at || '';
          valB = b.created_at || '';
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const groupedTasks = {
    pending: sortTasks(filteredTasks.filter((t) => t.status === 'pending')),
    in_progress: sortTasks(filteredTasks.filter((t) => t.status === 'in_progress')),
    completed: sortTasks(filteredTasks.filter((t) => t.status === 'completed')),
  };

  // Get unique assigned users for filter
  const assignedUserIds = new Set();
  tasksList.forEach(task => {
    (task.assigned_to || []).forEach(id => assignedUserIds.add(id));
  });
  const usersWithTasks = usersList.filter(u => assignedUserIds.has(u.user_id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Tareas</h1>
          <p className="text-muted-foreground">Gestión de tareas del equipo</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-tasks-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="create-task-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Tarea
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        {/* Status Filter */}
        <div className="flex gap-2">
          {['all', 'pending', 'in_progress', 'completed'].map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status)}
              data-testid={`filter-${status}`}
            >
              {status === 'all' ? 'Todas' : statusLabels[status]}
            </Button>
          ))}
        </div>

        <div className="h-6 w-px bg-border" />

        {/* Vertical Filter */}
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-muted-foreground" />
          <Select value={verticalFilter} onValueChange={setVerticalFilter}>
            <SelectTrigger className="w-[160px] h-8" data-testid="vertical-filter">
              <SelectValue placeholder="Vertical" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las verticales</SelectItem>
              {Object.entries(VERTICALS).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* User Filter */}
        {usersWithTasks.length > 0 && (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-[160px] h-8" data-testid="user-filter">
                <SelectValue placeholder="Colaborador" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los usuarios</SelectItem>
                {usersWithTasks.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Client/Empresa Filter */}
        <div className="flex items-center gap-2">
          <Building className="h-4 w-4 text-muted-foreground" />
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-[160px] h-8" data-testid="client-filter">
              <SelectValue placeholder="Empresa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las empresas</SelectItem>
              <SelectItem value="none">Sin empresa</SelectItem>
              {clientsList.map((c) => (
                <SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Active filters indicator */}
        {(verticalFilter !== 'all' || userFilter !== 'all' || clientFilter !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setVerticalFilter('all'); setUserFilter('all'); setClientFilter('all'); }}
            className="text-muted-foreground"
          >
            <Filter className="h-3 w-3 mr-1" />
            Limpiar filtros
          </Button>
        )}

        <div className="h-6 w-px bg-border" />

        {/* Sort control */}
        <div className="flex items-center gap-2">
          <Select value={sortField} onValueChange={(v) => setSortField(v)}>
            <SelectTrigger className="w-[140px] h-8" data-testid="task-sort-field">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Fecha creacion</SelectItem>
              <SelectItem value="due_date">Fecha limite</SelectItem>
              <SelectItem value="priority">Prioridad</SelectItem>
              <SelectItem value="title">Titulo</SelectItem>
              <SelectItem value="client_name">Empresa</SelectItem>
              <SelectItem value="assigned">Persona asignada</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
            data-testid="task-sort-dir"
            className="px-2"
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </Button>
        </div>
      </div>

      {/* Kanban-style columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Pending */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-rose-500" />
            <h2 className="font-heading font-semibold uppercase tracking-wide">
              Pendientes ({groupedTasks.pending.length})
            </h2>
          </div>
          <div className="space-y-3">
            {groupedTasks.pending.map((task) => (
              <TaskCard
                key={task.task_id}
                task={task}
                onUpdate={handleUpdate}
                onRequestDelete={handleRequestDelete}
                onStartTimer={handleStartTimer}
                onOpenDetail={handleOpenDetail}
                usersList={usersList}
                currentUser={user}
              />
            ))}
            {groupedTasks.pending.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No hay tareas pendientes
              </p>
            )}
          </div>
        </div>

        {/* In Progress */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <h2 className="font-heading font-semibold uppercase tracking-wide">
              En Progreso ({groupedTasks.in_progress.length})
            </h2>
          </div>
          <div className="space-y-3">
            {groupedTasks.in_progress.map((task) => (
              <TaskCard
                key={task.task_id}
                task={task}
                onUpdate={handleUpdate}
                onRequestDelete={handleRequestDelete}
                onStartTimer={handleStartTimer}
                onOpenDetail={handleOpenDetail}
                usersList={usersList}
                currentUser={user}
              />
            ))}
            {groupedTasks.in_progress.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No hay tareas en progreso
              </p>
            )}
          </div>
        </div>

        {/* Completed */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <h2 className="font-heading font-semibold uppercase tracking-wide">
              Completadas ({groupedTasks.completed.length})
            </h2>
          </div>
          <div className="space-y-3">
            {groupedTasks.completed.map((task) => (
              <TaskCard
                key={task.task_id}
                task={task}
                onUpdate={handleUpdate}
                onRequestDelete={handleRequestDelete}
                onStartTimer={handleStartTimer}
                onOpenDetail={handleOpenDetail}
                usersList={usersList}
                currentUser={user}
              />
            ))}
            {groupedTasks.completed.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No hay tareas completadas
              </p>
            )}
          </div>
        </div>
      </div>

      <CreateTaskDialog
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) setPrefilledClientId(''); }}
        onSubmit={handleCreate}
        usersList={usersList}
        clientsList={clientsList}
        prefilledClientId={prefilledClientId}
      />

      {/* Task Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailTask && (() => {
            const t = detailTask;
            const isCreator = t.created_by === user?.user_id;
            const isSupervisor = (t.supervisors || []).includes(user?.user_id);
            const isIovar = user?.email === 'imedina@transtar.io';
            const isAssigned = (t.assigned_to || []).includes(user?.user_id);
            const canChangeStatus = isCreator || user?.role === 'admin' || isSupervisor || isIovar;
            const canEdit = canChangeStatus || isAssigned;
            const daysSince = Math.floor((Date.now() - new Date(t.created_at).getTime()) / (1000*60*60*24));
            const pendingReqs = (t.status_change_requests || []).filter(r => r.status === 'pending');
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">{t.title}</DialogTitle>
                  <p className="text-sm text-muted-foreground">{t.description}</p>
                </DialogHeader>

                {/* Info badges */}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className={`badge-${t.status === 'completed' ? 'success' : t.status === 'in_progress' ? 'warning' : 'danger'}`}>{statusLabels[t.status]}</Badge>
                  <Badge variant="outline" className={`badge-${getPriorityColor(t.priority)}`}>{priorityLabels[t.priority]}</Badge>
                  {t.vertical && VERTICALS[t.vertical] && <Badge variant="outline" className={VERTICALS[t.vertical].color}><Briefcase className="h-3 w-3 mr-1" />{VERTICALS[t.vertical].label}</Badge>}
                  {t.client_name && <Badge variant="outline"><Building className="h-3 w-3 mr-1" />{t.client_name}</Badge>}
                </div>

                {/* Time info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card><CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Dias activa</p>
                    <p className="text-xl font-bold">{daysSince}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Tiempo trabajado</p>
                    <p className="text-xl font-bold">{t.actual_hours > 0 ? formatDuration(t.actual_hours * 60) : '0h'}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Creada</p>
                    <p className="text-sm font-medium">{formatDate(t.created_at)}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Fecha limite</p>
                    <p className="text-sm font-medium">{t.due_date ? formatDate(t.due_date) : 'Sin fecha'}</p>
                  </CardContent></Card>
                </div>

                {/* People */}
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Creada por:</span> <span className="font-medium">{t.created_by_name || t.created_by}</span></div>
                  <div><span className="text-muted-foreground">Supervisores:</span> <span className="font-medium">{(t.supervisor_names || []).join(', ') || 'Iovar Medina'}</span></div>
                  <div><span className="text-muted-foreground">Asignado a:</span> <span className="font-medium">{(t.assigned_users || []).map(u => u.name).join(', ') || 'Sin asignar'}</span></div>
                </div>

                {/* Status change - creator can change directly */}
                {canChangeStatus && t.status !== 'completed' && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Cambiar estatus:</span>
                    {['pending', 'in_progress', 'completed'].filter(s => s !== t.status).map(s => (
                      <Button key={s} size="sm" variant="outline" onClick={() => { handleUpdate(t.task_id, { status: s }); setDetailOpen(false); }}>
                        {statusLabels[s]}
                      </Button>
                    ))}
                  </div>
                )}

                {/* Status change requests for non-creators */}
                {!canChangeStatus && t.status !== 'completed' && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <p className="text-sm font-semibold">Solicitar cambio de estatus</p>
                    <div className="flex gap-2">
                      <Select value={statusRequestTarget} onValueChange={setStatusRequestTarget}>
                        <SelectTrigger className="w-[150px] h-8"><SelectValue placeholder="Nuevo estatus" /></SelectTrigger>
                        <SelectContent>
                          {['pending', 'in_progress', 'completed'].filter(s => s !== t.status).map(s => (
                            <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input placeholder="Razonamiento..." value={statusRequestReason} onChange={(e) => setStatusRequestReason(e.target.value)} className="h-8 flex-1" />
                      <Button size="sm" onClick={handleRequestStatusChange} disabled={!statusRequestTarget || !statusRequestReason}><Send className="h-3 w-3 mr-1" />Solicitar</Button>
                    </div>
                  </div>
                )}

                {/* Pending status change requests (for creator) */}
                {canChangeStatus && pendingReqs.length > 0 && (
                  <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2">
                    <p className="text-sm font-semibold text-amber-800">Solicitudes de cambio pendientes</p>
                    {pendingReqs.map(r => (
                      <div key={r.request_id} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                        <div>
                          <span className="font-medium">{r.requested_by_name}</span> solicita: <Badge variant="outline">{statusLabels[r.requested_status]}</Badge>
                          <p className="text-xs text-muted-foreground mt-0.5">Razon: {r.reason}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="default" className="h-7" onClick={() => handleApproveStatusChange(t.task_id, r.request_id, true)}>Aprobar</Button>
                          <Button size="sm" variant="outline" className="h-7" onClick={() => handleApproveStatusChange(t.task_id, r.request_id, false)}>Rechazar</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold flex items-center gap-1"><MessageSquare className="h-4 w-4" />Notas</Label>
                    {editingNotes !== (t.notes || '') && (
                      <Button size="sm" onClick={handleSaveNotes}>Guardar Notas</Button>
                    )}
                  </div>
                  <Textarea value={editingNotes} onChange={(e) => setEditingNotes(e.target.value)} rows={3} placeholder="Escribe notas sobre esta tarea..." />
                </div>

                {/* Notes History */}
                {(t.notes_history || []).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground">Historial de notas ({t.notes_history.length} cambios)</p>
                    <div className="max-h-32 overflow-y-auto space-y-1.5">
                      {[...t.notes_history].reverse().map((h, idx) => (
                        <div key={idx} className="text-xs border rounded p-2 bg-muted/30">
                          <div className="flex justify-between">
                            <span className="font-medium">{h.user_name}</span>
                            <span className="text-muted-foreground">{formatDate(h.changed_at)}</span>
                          </div>
                          <p className="text-muted-foreground mt-0.5">{h.new_notes?.slice(0, 100)}{h.new_notes?.length > 100 ? '...' : ''}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delete - only creator */}
                {isCreator && (
                  <div className="pt-2 border-t">
                    <Button variant="destructive" size="sm" onClick={() => { setDetailOpen(false); setTaskToDelete(t); setDeleteDialogOpen(true); }}>
                      <Trash2 className="h-4 w-4 mr-2" />Eliminar Tarea
                    </Button>
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        itemType="task"
        itemId={taskToDelete?.task_id}
        itemName={taskToDelete?.title}
        isAdmin={isAdmin}
        onDeleted={loadData}
        directDelete={handleDelete}
      />
    </div>
  );
}
